import { Plugin } from "@tiptap/pm/state"
import Image from "@tiptap/extension-image"

// Custom Image extension with drag-drop/paste upload, resize, and alignment
const ImageUpload = Image.extend({
  name: "image",

  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null, parseHTML: el => el.getAttribute("width") || el.style.width || null, renderHTML: attrs => attrs.width ? { width: attrs.width, style: `width: ${attrs.width}` } : {} },
      alignment: { default: "center", parseHTML: el => el.getAttribute("data-alignment") || "center", renderHTML: attrs => ({ "data-alignment": attrs.alignment }) },
    }
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const wrapper = document.createElement("div")
      wrapper.className = `tiptap-image-wrapper tiptap-image-align-${node.attrs.alignment || "center"}`
      wrapper.contentEditable = "false"

      const img = document.createElement("img")
      img.src = node.attrs.src
      img.alt = node.attrs.alt || ""
      if (node.attrs.width) { img.style.width = node.attrs.width; img.setAttribute("width", node.attrs.width) }

      // Controls (shown on click)
      const controls = document.createElement("div")
      controls.className = "tiptap-image-controls"
      controls.style.display = "none"

      // Size buttons
      const sizes = [
        { label: "S", value: "25%" },
        { label: "M", value: "50%" },
        { label: "L", value: "75%" },
        { label: "Full", value: "100%" },
      ]
      const sizeGroup = document.createElement("div")
      sizeGroup.className = "tiptap-image-control-group"
      sizes.forEach(s => {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "tiptap-image-control-btn"
        btn.textContent = s.label
        btn.title = s.value
        if (node.attrs.width === s.value) btn.classList.add("active")
        btn.addEventListener("click", (e) => {
          e.stopPropagation()
          editor.chain().focus().setNodeSelection(getPos()).updateAttributes("image", { width: s.value }).run()
        })
        sizeGroup.appendChild(btn)
      })
      controls.appendChild(sizeGroup)

      // Alignment buttons
      const alignments = [
        { icon: "fa-align-left", value: "left" },
        { icon: "fa-align-center", value: "center" },
        { icon: "fa-align-right", value: "right" },
      ]
      const alignGroup = document.createElement("div")
      alignGroup.className = "tiptap-image-control-group"
      alignments.forEach(a => {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "tiptap-image-control-btn"
        btn.innerHTML = `<span class="fas ${a.icon}"></span>`
        btn.title = a.value.charAt(0).toUpperCase() + a.value.slice(1)
        if (node.attrs.alignment === a.value) btn.classList.add("active")
        btn.addEventListener("click", (e) => {
          e.stopPropagation()
          editor.chain().focus().setNodeSelection(getPos()).updateAttributes("image", { alignment: a.value }).run()
        })
        alignGroup.appendChild(btn)
      })
      controls.appendChild(alignGroup)

      wrapper.appendChild(controls)
      wrapper.appendChild(img)

      // Show controls on click
      wrapper.addEventListener("click", (e) => {
        e.stopPropagation()
        // Hide all other image controls
        document.querySelectorAll(".tiptap-image-controls").forEach(c => c.style.display = "none")
        document.querySelectorAll(".tiptap-image-wrapper").forEach(w => w.classList.remove("selected"))
        controls.style.display = "flex"
        wrapper.classList.add("selected")
        editor.commands.setNodeSelection(getPos())
      })

      // Hide controls on click outside
      const hideControls = (e) => {
        if (!wrapper.contains(e.target)) {
          controls.style.display = "none"
          wrapper.classList.remove("selected")
        }
      }
      document.addEventListener("click", hideControls)

      return {
        dom: wrapper,
        destroy() { document.removeEventListener("click", hideControls) },
        update(updatedNode) {
          if (updatedNode.type.name !== "image") return false
          img.src = updatedNode.attrs.src
          img.alt = updatedNode.attrs.alt || ""
          if (updatedNode.attrs.width) { img.style.width = updatedNode.attrs.width; img.setAttribute("width", updatedNode.attrs.width) }
          else { img.style.width = ""; img.removeAttribute("width") }
          wrapper.className = `tiptap-image-wrapper tiptap-image-align-${updatedNode.attrs.alignment || "center"}`
          if (controls.style.display !== "none") wrapper.classList.add("selected")
          // Update active states
          sizeGroup.querySelectorAll(".tiptap-image-control-btn").forEach((btn, i) => {
            btn.classList.toggle("active", updatedNode.attrs.width === sizes[i].value)
          })
          alignGroup.querySelectorAll(".tiptap-image-control-btn").forEach((btn, i) => {
            btn.classList.toggle("active", updatedNode.attrs.alignment === alignments[i].value)
          })
          return true
        },
      }
    }
  },

  addProseMirrorPlugins() {
    const MAX_SIZE = 10 * 1024 * 1024 // 10MB

    const removePlaceholder = (view, placeholderText) => {
      if (view.isDestroyed) return null
      const { state } = view
      let from = null, to = null
      state.doc.descendants((node, nodePos) => {
        if (node.isText && node.text && node.text.includes(placeholderText)) {
          from = nodePos + node.text.indexOf(placeholderText); to = from + placeholderText.length; return false
        }
      })
      if (from !== null) {
        view.dispatch(state.tr.delete(from, to))
      }
      return from
    }

    const upload = (file, view, pos) => {
      if (!/\.(jpe?g|png|gif)$/i.test(file.name) && !/^image\/(jpeg|png|gif)$/.test(file.type)) return

      if (file.size > MAX_SIZE) {
        console.warn("[Tiptap] Image too large:", (file.size / 1024 / 1024).toFixed(1), "MB")
        return
      }

      // Unique placeholder per upload
      const uploadId = Date.now() + "-" + Math.random().toString(36).slice(2, 6)
      const placeholderText = `⏳ Uploading image (${uploadId})...`
      view.dispatch(view.state.tr.insertText(placeholderText, pos))

      const formData = new FormData()
      formData.append("files[]", file)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const postSlug = window.location.pathname.match(/posts\/([^/]+)/)?.[1]
        || document.querySelector("input[name='post[uuid]']")?.value
      const uploadUrl = postSlug ? `/posts/${postSlug}/images/upload.json` : "/images/upload.json"

      fetch(uploadUrl, { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: formData })
        .then(res => {
          if (!res.ok) throw new Error(`Upload failed (${res.status})`)
          return res.json()
        })
        .then(data => {
          const url = data.files?.[0]?.url || data.url || data.link
          const from = removePlaceholder(view, placeholderText)
          if (from !== null && url) {
            view.dispatch(view.state.tr.insert(from, view.state.schema.nodes.image.create({ src: url })))
          }
        })
        .catch(err => {
          console.error("[Tiptap] Image upload failed:", err)
          removePlaceholder(view, placeholderText)
        })
    }

    return [
      new Plugin({
        props: {
          handleDrop(view, event) {
            const files = event.dataTransfer?.files
            if (!files || files.length === 0) return false
            const images = Array.from(files).filter(f => f.type.startsWith("image/"))
            if (images.length === 0) return false
            event.preventDefault()
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos || view.state.selection.from
            images.forEach(file => upload(file, view, pos))
            return true
          },
          handlePaste(view, event) {
            const items = event.clipboardData?.items
            if (!items) return false
            const images = Array.from(items).filter(i => i.type.startsWith("image/"))
            if (images.length === 0) return false
            event.preventDefault()
            images.forEach(item => { const file = item.getAsFile(); if (file) upload(file, view, view.state.selection.from) })
            return true
          },
        },
      }),
    ]
  },

  parseHTML() {
    return [
      { tag: "img[src]" },
      { tag: ".medium-insert-images img", getAttrs: el => ({ src: el.getAttribute("src"), alt: el.getAttribute("alt") || "" }) },
      { tag: "figure img", getAttrs: el => ({ src: el.getAttribute("src"), alt: el.getAttribute("alt") || "" }) },
    ]
  },
})

export default ImageUpload
